import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Typography, useTheme } from '@mui/material';

function ChatPage({ messages, setMessages }) {
  const theme = useTheme();
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesContainerRef = useRef(null);

  useEffect(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, isLoading]);

  const handleSendMessage = async () => {
    if (!input.trim()) return;

    const userMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const history = messages.map(({ role, content }) => ({ role, content }));
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: input.trim(),
          history,
        })
      });

      const data = await response.json();
      if (!response.ok) {
        if (response.status === 429) {
          throw new Error(data.error || 'Too many requests. Please wait a moment.');
        }
        throw new Error(data.details || data.error || data.detail || 'Request failed');
      }
      setMessages(prev => [...prev, { role: 'assistant', content: data.answer }]);
    } catch (error) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: error.message || 'Couldn\'t connect to the server. Check if the backend is running.'
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  // minimal helper to pick readable colors
  const getBubbleColors = (role) => {
    if (role === 'user') {
      const bg = theme.palette.primary.main;
      return { bgcolor: bg, color: theme.palette.getContrastText(bg) };
    } else {
      // assistant / system bubble: darker neutral in dark mode, light neutral in light mode
      const bg = theme.palette.mode === 'dark' ? '#2b2f36' : '#f0f2f4';
      // use explicit readable color
      const color = theme.palette.mode === 'dark' ? '#e8eef8' : '#0b1220';
      return { bgcolor: bg, color };
    }
  };

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto' }}>
      <Typography variant="h4" component="h4" gutterBottom>
        Talk to me
      </Typography>

      <Box sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        height: 400,
        display: 'flex',
        flexDirection: 'column',
        mt: 3
      }}>
        <Box
          ref={messagesContainerRef}
          sx={{
          flex: 1,
          overflowY: 'auto',
          p: 2,
          bgcolor: 'background.default'
        }}>
          {messages.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="body1" color="text.secondary">
                Start a conversation! Ask me anything.
              </Typography>
            </Box>
          ) : (
            messages.map((msg, idx) => {
              const styles = getBubbleColors(msg.role); // <-- minimal change usage
              return (
                <Box
                  key={idx}
                  sx={{
                    mb: 2,
                    display: 'flex',
                    justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start'
                  }}
                >
                  <Box
                    sx={{
                      maxWidth: '70%',
                      p: 2,
                      borderRadius: 2,
                      bgcolor: styles.bgcolor,
                      color: styles.color,
                      wordBreak: 'break-word'
                    }}
                  >
                    <Typography variant="body1" sx={{ color: 'inherit' }}>{msg.content}</Typography>
                  </Box>
                </Box>
              );
            })
          )}
          {isLoading && (
            <Box sx={{ display: 'flex', gap: 1, p: 2 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'grey.400', animation: 'pulse 1.5s ease-in-out infinite' }} />
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'grey.400', animation: 'pulse 1.5s ease-in-out 0.2s infinite' }} />
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'grey.400', animation: 'pulse 1.5s ease-in-out 0.4s infinite' }} />
            </Box>
          )}
        </Box>

        <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ 
            display: 'flex', 
            gap: 1, 
            '& input::placeholder': {
              color: theme.palette.text.secondary,
              opacity: 1, // Firefox
            },
          }}>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
              placeholder="Type your message..."
              style={{
                flex: 1,
                padding: '12px',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: '4px',
                fontSize: '16px',
                background: theme.palette.mode === 'dark' ? '#0c0d10' : '#fff',
                color: theme.palette.text.primary, 
              }}
            />
            <Button type="send"
              variant="contained"
              onClick={handleSendMessage}
              disabled={isLoading}
              sx={{ minWidth: 'auto', px: 1 }}
            >
                <img src={'https://img.icons8.com/?size=100&id=124435&format=png&color=000000'} alt={'send'} width="32" height="32" />
            </Button>
          </Box>
        </Box>
      </Box>

      {/* minimal placeholder color fix */}
      <style>{`
        input::placeholder {
          color: ${theme.palette.mode === 'dark' ? '#ffffffff' : '#000000ff'};
          opacity: 1;
        }
        input:focus {
          outline: none;
          box-shadow: 0 0 0 3px rgba(59,130,246,0.12);
        }
      `}</style>
    </Box>
  );
}

export default ChatPage;
